import React from "react";
import {
  FaBuilding,
  FaEnvelope,
  FaExternalLinkAlt,
  FaGlobe,
  FaMapMarkerAlt,
  FaPhone,
  FaStickyNote,
  FaUserTie,
  FaUsers,
} from "react-icons/fa";
import { Modal } from "./ui";
import { fmtDateFR, parseEmails, toAbsolute } from "../utils";

export default function QuickViewModal({ data, onClose }) {
  const emails = parseEmails(data.email || "");
  const siteUrl = data.site_url ? toAbsolute(data.site_url) : "";
  const facebookUrl = data.facebook_url ? toAbsolute(data.facebook_url) : "";

  return (
    <Modal onClose={onClose} title={data.nom_club || "Aperçu du prospect"}>
      <div className="space-y-5">
        <div className="rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950 text-white">
          <div className="p-5">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-xs text-white/80">
                  <FaBuilding />
                  Prospect
                </div>

                <h3 className="mt-3 text-2xl font-bold leading-tight">
                  {data.nom_club || "—"}
                </h3>

                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  <Badge icon={<FaUsers />} text={data.discipline || "—"} />
                  <Badge
                    icon={<FaMapMarkerAlt />}
                    text={data.departement_code || "—"}
                  />
                  <Badge
                    text={
                      Number.isFinite(+data.nb_licencies)
                        ? `${data.nb_licencies} licencié(s)`
                        : "Licenciés non renseignés"
                    }
                  />
                </div>
              </div>

              <StatusBadge status={data.statut || "—"} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <InfoCard title="Contact" icon={<FaUserTie />}>
            <InfoLine label="Nom" value={data.contact_nom || "—"} />
            <InfoLine label="Fonction" value={data.fonction || "—"} />

            <div className="mt-3 flex flex-wrap gap-2">
              {emails.length ? (
                emails.map((email) => (
                  <a
                    key={email}
                    href={`mailto:${email}`}
                    className="inline-flex items-center gap-2 rounded-xl bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200 px-3 py-2 text-xs hover:bg-indigo-100 dark:hover:bg-indigo-900/50"
                  >
                    <FaEnvelope />
                    {email}
                  </a>
                ))
              ) : (
                <span className="text-sm text-slate-500">Aucun email</span>
              )}

              {data.telephone ? (
                <a
                  href={`tel:${data.telephone}`}
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200 px-3 py-2 text-xs hover:bg-emerald-100 dark:hover:bg-emerald-900/50"
                >
                  <FaPhone />
                  {data.telephone}
                </a>
              ) : null}
            </div>
          </InfoCard>

          <InfoCard title="Suivi prospection" icon={<FaStickyNote />}>
            <InfoLine label="Statut" value={data.statut || "—"} />
            <InfoLine
              label="Dernier contact"
              value={fmtDateFR(data.date_contact) || "—"}
            />
            <InfoLine label="Statut site" value={data.site_status || "—"} />

            {data._isUnsub && (
              <div className="mt-3 rounded-2xl bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-900/50 p-3 text-sm text-rose-700 dark:text-rose-100">
                Contact détecté comme désinscrit
                {data._unsubDate ? ` le ${fmtDateFR(data._unsubDate)}` : ""}.
              </div>
            )}
          </InfoCard>

          <InfoCard title="Présence web" icon={<FaGlobe />}>
            {siteUrl ? (
              <a
                href={siteUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-xl bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200 px-3 py-2 text-sm hover:bg-blue-100 dark:hover:bg-blue-900/50"
              >
                <FaExternalLinkAlt />
                Voir le site
              </a>
            ) : (
              <div className="text-sm text-slate-500">Aucun site renseigné</div>
            )}

            {facebookUrl ? (
              <a
                href={facebookUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-2 rounded-xl bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200 px-3 py-2 text-sm hover:bg-slate-200 dark:hover:bg-slate-700"
              >
                <FaExternalLinkAlt />
                Facebook
              </a>
            ) : null}
          </InfoCard>
        </div>

        <div className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <div className="flex items-center gap-2 font-semibold">
            <FaStickyNote className="text-slate-400" />
            Observations
          </div>

          <div className="mt-3 rounded-2xl bg-slate-50 dark:bg-slate-800/70 p-4 text-sm leading-relaxed whitespace-pre-wrap text-slate-700 dark:text-slate-200 min-h-[80px]">
            {data.observations || "Aucune observation renseignée."}
          </div>
        </div>
      </div>
    </Modal>
  );
}

function InfoCard({ title, icon, children }) {
  return (
    <div className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
      <div className="flex items-center gap-2 font-semibold text-slate-900 dark:text-slate-100">
        <span className="text-slate-400">{icon}</span>
        {title}
      </div>

      <div className="mt-4 space-y-2">{children}</div>
    </div>
  );
}

function InfoLine({ label, value }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-3 text-sm">
      <div className="text-slate-500 dark:text-slate-400">{label}</div>
      <div className="font-medium text-slate-800 dark:text-slate-100 break-words">
        {value || "—"}
      </div>
    </div>
  );
}

function Badge({ icon, text }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1">
      {icon}
      {text}
    </span>
  );
}

function StatusBadge({ status }) {
  const className = getStatusClass(status);

  return (
    <span
      className={[
        "inline-flex items-center justify-center rounded-2xl px-4 py-2 text-sm font-semibold",
        className,
      ].join(" ")}
    >
      {status || "—"}
    </span>
  );
}

function getStatusClass(status) {
  switch (status) {
    case "Client":
      return "bg-emerald-100 text-emerald-700";
    case "Prospect +":
      return "bg-orange-100 text-orange-700";
    case "Prospect":
      return "bg-amber-100 text-amber-700";
    case "Intérêt":
      return "bg-yellow-100 text-yellow-700";
    case "Perdu":
      return "bg-rose-100 text-rose-700";
    case "Désinscrit":
      return "bg-slate-200 text-slate-700";
    case "Mail invalide":
      return "bg-purple-100 text-purple-700";
    default:
      return "bg-white/10 text-white border border-white/15";
  }
}